
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import {
  Lightbulb, Plus, ChevronUp, Search, TrendingUp,
  CheckCircle2, Clock, XCircle, MessageSquare, Flame
} from "lucide-react";

const CATEGORIES = [
  { value: "accounting", label: "Accounting" },
  { value: "gst", label: "GST & Tax" },
  { value: "invoicing", label: "Invoicing" },
  { value: "reports", label: "Reports" },
  { value: "integrations", label: "Integrations" },
  { value: "ui", label: "UI/UX" },
  { value: "mobile", label: "Mobile" },
  { value: "general", label: "General" },
];

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  open: { label: "Open", color: "bg-primary/10 text-primary", icon: Lightbulb },
  under_review: { label: "Under Review", color: "bg-amber-500/10 text-amber-600", icon: Clock },
  planned: { label: "Planned", color: "bg-blue-500/10 text-blue-600", icon: TrendingUp },
  in_progress: { label: "In Progress", color: "bg-purple-500/10 text-purple-600", icon: Flame },
  completed: { label: "Completed", color: "bg-emerald-500/10 text-emerald-600", icon: CheckCircle2 },
  declined: { label: "Declined", color: "bg-muted text-muted-foreground", icon: XCircle },
};

const SORT_OPTIONS = [
  { value: "votes", label: "Most Voted" },
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

export default function FeatureRequestsPage() {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = role === "super_admin" || role === "company_admin";
  const [showSubmit, setShowSubmit] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("votes");
  const [form, setForm] = useState({ title: "", description: "", category: "general" });

  const { data: requests = [] } = useQuery({
    queryKey: ["feature-requests"],
    queryFn: async () => {
      const { data, error } = await supabase.from("feature_requests").select("*").order("upvotes", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: userVotes = [] } = useQuery({
    queryKey: ["feature-votes"],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase.from("feature_request_votes").select("feature_request_id").eq("user_id", user.id);
      if (error) throw error;
      return data.map((v: any) => v.feature_request_id);
    },
    enabled: !!user,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not logged in");
      const { error } = await supabase.from("feature_requests").insert({
        author_user_id: user.id,
        author_name: user.name,
        author_role: role,
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Feature request submitted!" });
      setShowSubmit(false);
      setForm({ title: "", description: "", category: "general" });
      queryClient.invalidateQueries({ queryKey: ["feature-requests"] });
    },
    onError: () => toast({ title: "Error submitting request", variant: "destructive" }),
  });

  const voteMutation = useMutation({
    mutationFn: async (requestId: string) => {
      if (!user) throw new Error("Not logged in");
      const hasVoted = userVotes.includes(requestId);
      if (hasVoted) {
        await supabase.from("feature_request_votes").delete().eq("feature_request_id", requestId).eq("user_id", user.id);
      } else {
        const { error } = await supabase.from("feature_request_votes").insert({ feature_request_id: requestId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-requests"] });
      queryClient.invalidateQueries({ queryKey: ["feature-votes"] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status, response }: { id: string; status: string; response?: string }) => {
      const updates: any = { status, updated_at: new Date().toISOString() };
      if (response !== undefined) updates.admin_response = response;
      const { error } = await supabase.from("feature_requests").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Status updated" });
      queryClient.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });

  let filtered = requests
    .filter((r: any) => categoryFilter === "all" || r.category === categoryFilter)
    .filter((r: any) => statusFilter === "all" || r.status === statusFilter)
    .filter((r: any) => r.title.toLowerCase().includes(searchTerm.toLowerCase()) || r.description.toLowerCase().includes(searchTerm.toLowerCase()));

  if (sortBy === "newest") filtered = [...filtered].sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  else if (sortBy === "oldest") filtered = [...filtered].sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  else filtered = [...filtered].sort((a: any, b: any) => b.upvotes - a.upvotes);

  const topRequests = [...requests].sort((a: any, b: any) => b.upvotes - a.upvotes).slice(0, 5);
  const stats = {
    total: requests.length,
    open: requests.filter((r: any) => r.status === "open").length,
    planned: requests.filter((r: any) => r.status === "planned" || r.status === "in_progress").length,
    completed: requests.filter((r: any) => r.status === "completed").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Feature Requests</h1>
          <p className="text-muted-foreground text-sm">Submit ideas and vote on what to build next</p>
        </div>
        <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Submit Request</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Submit Feature Request</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="Feature title..." value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Textarea placeholder="Describe the feature you'd like to see..." rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={() => submitMutation.mutate()} disabled={!form.title.trim() || !form.description.trim()} className="w-full">Submit</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Requests", value: stats.total, icon: Lightbulb },
          { label: "Open", value: stats.open, icon: MessageSquare },
          { label: "Planned / In Progress", value: stats.planned, icon: TrendingUp },
          { label: "Completed", value: stats.completed, icon: CheckCircle2 },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className="h-8 w-8 text-primary" />
              <div><p className="text-2xl font-bold text-foreground">{s.value}</p><p className="text-xs text-muted-foreground">{s.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        {/* Main list */}
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search requests..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Button variant={categoryFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter("all")}>All</Button>
            {CATEGORIES.map((c) => (
              <Button key={c.value} variant={categoryFilter === c.value ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter(c.value)}>{c.label}</Button>
            ))}
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Button variant={statusFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setStatusFilter("all")}>All Status</Button>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <Button key={k} variant={statusFilter === k ? "default" : "outline"} size="sm" onClick={() => setStatusFilter(k)}>{v.label}</Button>
            ))}
          </div>

          {/* Request cards */}
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-12">No feature requests found. Submit the first one!</p>}
          {filtered.map((r: any) => {
            const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.open;
            const StatusIcon = sc.icon;
            const hasVoted = userVotes.includes(r.id);
            return (
              <Card key={r.id}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Vote button */}
                    <button
                      onClick={() => voteMutation.mutate(r.id)}
                      className={`flex flex-col items-center min-w-[56px] p-2 rounded-lg border transition-colors ${
                        hasVoted ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      <ChevronUp className="h-5 w-5" />
                      <span className="text-lg font-bold">{r.upvotes}</span>
                      <span className="text-[10px]">votes</span>
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-foreground text-sm">{r.title}</h3>
                        <Badge className={`${sc.color} text-[10px] h-5`}><StatusIcon className="h-3 w-3 mr-1" />{sc.label}</Badge>
                        <Badge variant="outline" className="text-[10px] h-5 capitalize">{r.category}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{r.description}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{r.author_name}</span>
                        <span>•</span>
                        <span>{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      {r.admin_response && (
                        <div className="mt-2 p-2.5 rounded-md bg-muted/50 border border-border">
                          <p className="text-xs font-medium text-foreground mb-0.5">Admin Response:</p>
                          <p className="text-xs text-muted-foreground">{r.admin_response}</p>
                        </div>
                      )}
                      {/* Admin controls */}
                      {isAdmin && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {Object.entries(STATUS_CONFIG).filter(([k]) => k !== r.status).map(([k, v]) => (
                            <Button key={k} variant="outline" size="sm" className="text-[10px] h-6 px-2" onClick={() => statusMutation.mutate({ id: r.id, status: k })}>
                              {v.label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Sidebar - Top Requested */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Flame className="h-4 w-4 text-primary" />Most Requested</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {topRequests.length === 0 && <p className="text-xs text-muted-foreground">No requests yet</p>}
              {topRequests.map((r: any, i: number) => (
                <div key={r.id} className="flex items-center gap-3">
                  <span className="text-lg font-bold text-muted-foreground w-6 text-center">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{r.title}</p>
                    <p className="text-[10px] text-muted-foreground">{r.upvotes} votes</p>
                  </div>
                  <Badge className={`${(STATUS_CONFIG[r.status] || STATUS_CONFIG.open).color} text-[10px] h-5`}>
                    {(STATUS_CONFIG[r.status] || STATUS_CONFIG.open).label}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">By Category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {CATEGORIES.map((c) => {
                const count = requests.filter((r: any) => r.category === c.value).length;
                if (count === 0) return null;
                return (
                  <div key={c.value} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{c.label}</span>
                    <Badge variant="secondary" className="text-[10px]">{count}</Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
